import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { SurveyData, SurveyQuestion, SurveyPage } from "../api/surveysApi";

// Generate unique IDs using crypto.randomUUID() for better uniqueness than Date.now()
const generateUniqueId = (prefix: string): string => {
  return `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
};

interface SurveyBuilderState {
  isEditing: boolean;
  currentSurveyId?: string;
  surveyData: SurveyData;
  currentPageIndex: number;
  currentQuestionIndex?: number;
  isDirty: boolean;
  isSaving: boolean;
  lastSavedData?: SurveyData;
}

const initialSurveyData: SurveyData = {
  pages: [
    {
      id: "page-1",
      title: "",
      description: "",
      questions: [],
    },
  ],
  settings: {
    progressBar: true,
    questionNumbering: false,
    allowBackButton: true,
    randomizeQuestions: false,
    randomizePages: false,
  },
};

const initialState: SurveyBuilderState = {
  isEditing: false,
  surveyData: initialSurveyData,
  currentPageIndex: 0,
  isDirty: false,
  isSaving: false,
};

const surveyBuilderSlice = createSlice({
  name: "surveyBuilder",
  initialState,
  reducers: {
    // ============================================
    // Initialization & Management
    // ============================================

    startEditingSurvey: (
      state,
      action: PayloadAction<{ surveyId: string; surveyData: SurveyData }>,
    ) => {
      state.isEditing = true;
      state.currentSurveyId = action.payload.surveyId;
      state.surveyData = action.payload.surveyData;
      state.lastSavedData = JSON.parse(JSON.stringify(action.payload.surveyData));
      state.isDirty = false;
      state.currentPageIndex = 0;
      state.currentQuestionIndex = undefined;
    },

    startCreatingSurvey: (state) => {
      state.isEditing = true;
      state.currentSurveyId = undefined;
      state.surveyData = JSON.parse(JSON.stringify(initialSurveyData));
      state.lastSavedData = undefined;
      state.isDirty = true;
      state.currentPageIndex = 0;
      state.currentQuestionIndex = undefined;
    },

    exitBuilder: (state) => {
      state.isEditing = false;
      state.currentSurveyId = undefined;
      state.surveyData = JSON.parse(JSON.stringify(initialSurveyData));
      state.lastSavedData = undefined;
      state.isDirty = false;
      state.currentPageIndex = 0;
      state.currentQuestionIndex = undefined;
      state.isSaving = false;
    },

    resetBuilder: (state) => {
      state.surveyData = JSON.parse(JSON.stringify(initialSurveyData));
      state.isDirty = true;
      state.currentPageIndex = 0;
      state.currentQuestionIndex = undefined;
    },

    // ============================================
    // Navigation
    // ============================================

    setCurrentPageIndex: (state, action: PayloadAction<number>) => {
      state.currentPageIndex = action.payload;
      state.currentQuestionIndex = undefined;
    },

    setCurrentQuestionIndex: (state, action: PayloadAction<number | undefined>) => {
      state.currentQuestionIndex = action.payload;
    },

    // ============================================
    // Page Management
    // ============================================

    updatePageTitle: (state, action: PayloadAction<string>) => {
      state.surveyData.pages[state.currentPageIndex].title = action.payload;
      state.isDirty = true;
    },

    updatePageDescription: (state, action: PayloadAction<string>) => {
      state.surveyData.pages[state.currentPageIndex].description = action.payload;
      state.isDirty = true;
    },

    addPage: (state) => {
      const newPageId = generateUniqueId("page");
      const newPage: SurveyPage = {
        id: newPageId,
        title: "",
        description: "",
        questions: [],
      };
      state.surveyData.pages.push(newPage);
      state.currentPageIndex = state.surveyData.pages.length - 1;
      state.currentQuestionIndex = undefined;
      state.isDirty = true;
    },

    deletePage: (state, action: PayloadAction<number>) => {
      const pageIndex = action.payload;
      if (state.surveyData.pages.length > 1) {
        state.surveyData.pages.splice(pageIndex, 1);
        if (state.currentPageIndex >= state.surveyData.pages.length) {
          state.currentPageIndex = state.surveyData.pages.length - 1;
        }
        state.currentQuestionIndex = undefined;
        state.isDirty = true;
      }
    },

    reorderPages: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      const { fromIndex, toIndex } = action.payload;
      const [removed] = state.surveyData.pages.splice(fromIndex, 1);
      state.surveyData.pages.splice(toIndex, 0, removed);
      if (state.currentPageIndex === fromIndex) {
        state.currentPageIndex = toIndex;
      }
      state.isDirty = true;
    },

    // ============================================
    // Question Management
    // ============================================

    addQuestion: (state) => {
      const questionId = generateUniqueId("q");
      const newQuestion: SurveyQuestion = {
        id: questionId,
        questionText: "",
        type: "short_text",
        required: false,
      };
      state.surveyData.pages[state.currentPageIndex].questions.push(newQuestion);
      state.currentQuestionIndex =
        state.surveyData.pages[state.currentPageIndex].questions.length - 1;
      state.isDirty = true;
    },

    updateQuestion: (state, action: PayloadAction<Partial<SurveyQuestion>>) => {
      if (state.currentQuestionIndex !== undefined) {
        const question =
          state.surveyData.pages[state.currentPageIndex].questions[state.currentQuestionIndex];
        Object.assign(question, action.payload);
        state.isDirty = true;
      }
    },

    deleteQuestion: (state, action: PayloadAction<number>) => {
      const questionIndex = action.payload;
      state.surveyData.pages[state.currentPageIndex].questions.splice(questionIndex, 1);
      if (state.currentQuestionIndex === questionIndex) {
        state.currentQuestionIndex = undefined;
      }
      state.isDirty = true;
    },

    reorderQuestions: (
      state,
      action: PayloadAction<{
        fromIndex: number;
        toIndex: number;
        fromPageIndex?: number;
        toPageIndex?: number;
      }>,
    ) => {
      const {
        fromIndex,
        toIndex,
        fromPageIndex = state.currentPageIndex,
        toPageIndex = state.currentPageIndex,
      } = action.payload;

      const [removed] = state.surveyData.pages[fromPageIndex].questions.splice(fromIndex, 1);
      state.surveyData.pages[toPageIndex].questions.splice(toIndex, 0, removed);

      state.isDirty = true;
    },

    duplicateQuestion: (state, action: PayloadAction<number>) => {
      const questionIndex = action.payload;
      const question = state.surveyData.pages[state.currentPageIndex].questions[questionIndex];
      const duplicatedQuestion: SurveyQuestion = {
        ...JSON.parse(JSON.stringify(question)),
        id: generateUniqueId("q"),
      };
      state.surveyData.pages[state.currentPageIndex].questions.splice(
        questionIndex + 1,
        0,
        duplicatedQuestion,
      );
      state.isDirty = true;
    },

    // ============================================
    // Survey Settings
    // ============================================

    updateSurveySettings: (
      state,
      action: PayloadAction<{
        progressBar?: boolean;
        questionNumbering?: boolean;
        allowBackButton?: boolean;
        randomizeQuestions?: boolean;
        randomizePages?: boolean;
      }>,
    ) => {
      if (!state.surveyData.settings) {
        state.surveyData.settings = {};
      }
      Object.assign(state.surveyData.settings, action.payload);
      state.isDirty = true;
    },

    // ============================================
    // Save State
    // ============================================

    markAsSaving: (state) => {
      state.isSaving = true;
    },

    markAsSaved: (state) => {
      state.isSaving = false;
      state.isDirty = false;
      state.lastSavedData = JSON.parse(JSON.stringify(state.surveyData));
    },

    markAsDirty: (state) => {
      state.isDirty = true;
    },
  },
});

export const {
  startEditingSurvey,
  startCreatingSurvey,
  exitBuilder,
  resetBuilder,
  setCurrentPageIndex,
  setCurrentQuestionIndex,
  updatePageTitle,
  updatePageDescription,
  addPage,
  deletePage,
  reorderPages,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  reorderQuestions,
  duplicateQuestion,
  updateSurveySettings,
  markAsSaving,
  markAsSaved,
  markAsDirty,
} = surveyBuilderSlice.actions;

export default surveyBuilderSlice.reducer;
